import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Read token from either persistent (localStorage = "keep me signed in")
// or per-tab (sessionStorage) storage. localStorage takes precedence.
const readStoredToken = () => {
  try {
    return localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
  } catch (e) { return null; }
};

const writeStoredToken = (token, persist) => {
  try {
    // Always clear both first to avoid stale tokens lingering
    localStorage.removeItem('session_token');
    sessionStorage.removeItem('session_token');
    if (!token) return;
    if (persist) {
      localStorage.setItem('session_token', token);
    } else {
      sessionStorage.setItem('session_token', token);
    }
  } catch (e) { /* ignore quota */ }
};

const clearStoredToken = () => {
  try {
    localStorage.removeItem('session_token');
    sessionStorage.removeItem('session_token');
  } catch (e) { /* ignore */ }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionToken, setSessionToken] = useState(() => readStoredToken());
  const [impersonating, setImpersonating] = useState(() => sessionStorage.getItem('impersonating_from') || null);

  const [hasInitialized, setHasInitialized] = useState(false);

  const checkAuth = useCallback(async () => {
    if (!sessionToken) {
      setLoading(false);
      return;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      
      const response = await fetch(`${API}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        },
        
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        setUser(data);
        setEmployee(data.employee);
      } else {
        clearStoredToken();
        setSessionToken(null);
        setUser(null);
        setEmployee(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      clearStoredToken();
      setSessionToken(null);
    } finally {
      setLoading(false);
    }
  }, [sessionToken]);

  // Only check auth on initial mount
  useEffect(() => {
    if (!hasInitialized) {
      checkAuth();
      setHasInitialized(true);
    }
  }, [hasInitialized, checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processSession = async (sessionId) => {
    try {
      const response = await fetch(`${API}/auth/session?session_id=${sessionId}`, {
        method: 'POST',
        
      });

      if (response.ok) {
        const data = await response.json();
        // Store session token for future requests
        const cookies = document.cookie.split(';');
        const sessionCookie = cookies.find(c => c.trim().startsWith('session_token='));
        if (sessionCookie) {
          const token = sessionCookie.split('=')[1];
          sessionStorage.setItem('session_token', token);
          setSessionToken(token);
        }
        setUser(data);
        setEmployee(data.employee);
        return data;
      }
      throw new Error('Session creation failed');
    } catch (error) {
      console.error('Process session error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        },
        
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearStoredToken();
      setSessionToken(null);
      setUser(null);
      setEmployee(null);
    }
  };

  const loginWithPassword = async (email, password, rememberMe = false) => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      
      const response = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        
        signal: controller.signal,
        body: JSON.stringify({ email, password, remember_me: !!rememberMe })
      });
      
      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        // Store session token: localStorage if "keep me signed in", else sessionStorage
        if (data.session_token) {
          writeStoredToken(data.session_token, !!rememberMe);
          setSessionToken(data.session_token);
        }
        // Remove password_hash and session_token from user data before storing
        const userData = {
          user_id: data.user_id,
          email: data.email,
          name: data.name,
          picture: data.picture
        };
        setUser(userData);
        setEmployee(data.employee);
        setLoading(false);
        return data;
      } else {
        // Handle error response
        try {
          const error = await response.json();
          throw new Error(error.detail || 'Login failed');
        } catch (parseError) {
          // If JSON parsing fails, use status text
          if (response.status === 401) {
            throw new Error('Invalid email or password');
          }
          throw new Error('Login failed');
        }
      }
    } catch (error) {
      console.error('Password login error:', error);
      throw error;
    }
  };

  const getAuthHeader = () => {
    return sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {};
  };

  const impersonateUser = async (targetEmail) => {
    const res = await fetch(`${API}/auth/impersonate`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ target_email: targetEmail }),
    });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.detail || 'Impersonation failed');
    }
    // Remember whether the original token was persistent (localStorage) or per-tab (sessionStorage)
    const wasPersistent = !!localStorage.getItem('session_token');
    // Store original session so we can return
    sessionStorage.setItem('impersonating_from', sessionToken);
    sessionStorage.setItem('original_user', JSON.stringify({ user, employee }));
    sessionStorage.setItem('impersonating_was_persistent', wasPersistent ? '1' : '0');
    // Switch to impersonated session (tab-scoped to keep impersonation transient)
    writeStoredToken(data.session_token, false);
    setSessionToken(data.session_token);
    setImpersonating(sessionToken);
    setUser({ user_id: data.user_id, email: data.email, name: data.name, picture: data.picture });
    setEmployee(data.employee);
    return data;
  };

  const stopImpersonating = () => {
    const originalToken = sessionStorage.getItem('impersonating_from');
    const originalData = JSON.parse(sessionStorage.getItem('original_user') || '{}');
    const wasPersistent = sessionStorage.getItem('impersonating_was_persistent') === '1';
    if (originalToken) {
      writeStoredToken(originalToken, wasPersistent);
      setSessionToken(originalToken);
      setUser(originalData.user);
      setEmployee(originalData.employee);
    }
    sessionStorage.removeItem('impersonating_from');
    sessionStorage.removeItem('original_user');
    sessionStorage.removeItem('impersonating_was_persistent');
    setImpersonating(null);
  };

  const adminResetPassword = async (targetEmail, newPassword) => {
    const res = await fetch(`${API}/auth/admin-reset-password`, {
      method: 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ target_email: targetEmail, new_password: newPassword }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Reset failed');
    }
    return await res.json();
  };

  const isAdmin = employee?.role === 'Admin Team' || employee?.role === 'Admin Head' || employee?.role === 'Super Admin';
  const isAdminHead = employee?.role === 'Admin Head' || employee?.role === 'Super Admin';
  const isManager = employee ? false : false;

  // Permission helpers — check against module_permissions on employee,
  // with Super Admin always having full access regardless.
  // A few modules are role-gated globally (e.g. Analytics is implicitly available
  // to leadership + IT roles even if `employee.permissions.analytics` was never seeded).
  const ROLE_DEFAULT_VIEW = {
    analytics: ['Super Admin', 'Admin Head', 'Management', 'IT Head', 'IT Team'],
    it_queue: ['Super Admin', 'Management', 'IT Head', 'IT Team'],
    escalated_requests: ['Super Admin', 'Management', 'IT Head', 'IT Team'],
    courier_details: ['Super Admin', 'Admin Head', 'Admin Team'],
  };

  const hasPermission = (moduleKey, action = 'view') => {
    if (!employee) return false;
    if (employee.role === 'Super Admin') return true;
    // Surveys tab: visible to Admin Head / Management / any employee with can_create_surveys
    if (moduleKey === 'surveys' && action === 'view') {
      if (['Admin Head', 'Management'].includes(employee.role)) return true;
      if (employee.can_create_surveys) return true;
      return false;
    }
    // Induction tab: HR dept members + Super Admin (full); Management/Admin Head view-only
    if (moduleKey === 'inductions' && action === 'view') {
      if (['Management', 'Admin Head'].includes(employee.role)) return true;
      if (['Human Resources', 'Human Resources Management'].includes(employee.department)) return true;
      return false;
    }
    // Role-based default view fallback for cross-cutting modules
    if (action === 'view' && ROLE_DEFAULT_VIEW[moduleKey]?.includes(employee.role)) return true;
    const perms = employee.permissions || {};
    const mod = perms[moduleKey];
    if (!mod) return false;
    return !!mod[action];
  };

  const canView = (moduleKey) => hasPermission(moduleKey, 'view');
  const canCreate = (moduleKey) => hasPermission(moduleKey, 'create');
  const canEdit = (moduleKey) => hasPermission(moduleKey, 'edit');
  const canApprove = (moduleKey) => hasPermission(moduleKey, 'approve');

  const value = {
    user,
    employee,
    loading,
    isAuthenticated: !!user,
    isAdmin,
    isAdminHead,
    isManager,
    impersonating,
    login,
    loginWithPassword,
    logout,
    processSession,
    getAuthHeader,
    sessionToken,
    refreshAuth: checkAuth,
    impersonateUser,
    stopImpersonating,
    adminResetPassword,
    hasPermission,
    canView,
    canCreate,
    canEdit,
    canApprove,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
